<script setup lang="ts">
import AlarmReportChart from './AlarmReportChart.vue'
import AlarmReportSection from '../AlarmReportSection.vue'

import {
  IAlarmByGroup,
  IAlarmBySensor,
  IAlarmByType,
  IAlarmReportMetrics,
  IAlarmByClassification,
  IClassificationMetrics,
  IAlarmDurationByType,
  IAlarmByShift,
  IAlarmByNature,
} from '@/interfaces/stores/alarm'

import {
  filterDefinedAlarmsByClassification,
  filterDefinedClassificationDurationMetrics,
  shouldShowAlarmsBySensorChart,
} from '@/utils/components/alarm/alarm-report-visibility'

import { useAlarmStore } from '@/store/alarm'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  alarmReportMetrics: IAlarmReportMetrics
}>()

const { getReportAlarmTypeKeys } = storeToRefs(useAlarmStore())

const hasGroupCharts = computed(
  () =>
    (props.alarmReportMetrics.alarms_by_group?.length ?? 0) > 0 ||
    (props.alarmReportMetrics.group_alarms_by_type?.length ?? 0) > 0,
)

const showAlarmsBySensorChart = computed(() =>
  shouldShowAlarmsBySensorChart(
    props.alarmReportMetrics.alarms_by_sensor,
    getReportAlarmTypeKeys.value,
  ),
)

const alarmsByClassification = computed(() =>
  filterDefinedAlarmsByClassification(props.alarmReportMetrics.alarms_by_classification),
)

const classificationMetrics = computed(() =>
  filterDefinedClassificationDurationMetrics(props.alarmReportMetrics.classification_metrics),
)

const hasClassificationCharts = computed(
  () => alarmsByClassification.value.length > 0 || classificationMetrics.value.length > 0,
)

const alarmsByNature = computed(() => props.alarmReportMetrics.alarms_by_nature ?? [])
</script>

<template>
  <div class="flex flex-col gap-6">
    <AlarmReportSection
      :title="t('alarm_report.body.result.sections.by_sensor.title')"
      :subtitle="t('alarm_report.body.result.sections.by_sensor.subtitle')"
      accent-color="#123ADD">
      <AlarmReportChart
        v-if="showAlarmsBySensorChart"
        :chart-title="t('alarm_report.body.result.charts.by_sensor.title')"
        chart-type="alarm-report-by-sensor-chart"
        :readings="props.alarmReportMetrics.alarms_by_sensor as IAlarmBySensor[]" />

      <AlarmReportChart
        :chart-title="t('alarm_report.body.result.charts.by_type.title')"
        chart-type="alarm-report-by-type-chart"
        :readings="props.alarmReportMetrics.alarms_by_type as IAlarmByType[]" />

      <AlarmReportChart
        :chart-title="t('alarm_report.body.result.charts.by_shift.title')"
        chart-type="alarm-report-by-shift-chart"
        :readings="props.alarmReportMetrics.alarms_by_shift as IAlarmByShift[]" />

      <AlarmReportChart
        :chart-title="t('alarm_report.body.result.charts.total_hours_by_type.title')"
        chart-type="alarm-duration-by-type-chart"
        :readings="props.alarmReportMetrics.alarm_duration_by_type as IAlarmDurationByType[]" />
    </AlarmReportSection>

    <AlarmReportSection
      v-if="hasGroupCharts"
      :title="t('alarm_report.body.result.sections.by_group.title')"
      :subtitle="t('alarm_report.body.result.sections.by_group.subtitle')"
      accent-color="#04304c">
      <AlarmReportChart
        v-if="(props.alarmReportMetrics.alarms_by_group?.length ?? 0) > 0"
        :chart-title="t('alarm_report.body.result.charts.by_group.title')"
        chart-type="alarm-report-by-group-chart"
        :readings="props.alarmReportMetrics.alarms_by_group as IAlarmByGroup[]" />

      <AlarmReportChart
        v-if="(props.alarmReportMetrics.group_alarms_by_type?.length ?? 0) > 0"
        :chart-title="t('alarm_report.body.result.charts.by_group_type.title')"
        chart-type="alarm-report-by-type-chart"
        :readings="props.alarmReportMetrics.group_alarms_by_type as IAlarmByType[]" />
    </AlarmReportSection>

    <AlarmReportSection
      v-if="hasClassificationCharts"
      :title="t('alarm_report.body.result.sections.by_classification.title')"
      :subtitle="t('alarm_report.body.result.sections.by_classification.subtitle')"
      accent-color="#0eca8b">
      <AlarmReportChart
        v-if="alarmsByClassification.length"
        :chart-title="t('alarm_report.body.result.charts.by_classification.title')"
        chart-type="alarm-report-by-classification-chart"
        :readings="alarmsByClassification as IAlarmByClassification[]" />

      <AlarmReportChart
        v-if="classificationMetrics.length"
        :chart-title="t('alarm_report.body.result.charts.average_hours_by_classification.title')"
        chart-type="alarm-report-average-hours-by-classification-chart"
        :readings="classificationMetrics as IClassificationMetrics[]" />

      <AlarmReportChart
        v-if="classificationMetrics.length"
        :chart-title="t('alarm_report.body.result.charts.total_hours_by_classification.title')"
        chart-type="alarm-report-total-hours-by-classification-chart"
        :readings="classificationMetrics as IClassificationMetrics[]" />

      <AlarmReportChart
        v-if="alarmsByNature.length"
        :chart-title="t('alarm_report.body.result.charts.by_nature.title')"
        chart-type="alarm-report-by-nature-chart"
        :readings="alarmsByNature as IAlarmByNature[]" />
    </AlarmReportSection>
  </div>
</template>
